import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";

export interface EncounterActiveSession {
  isActive: boolean;
  providerId: number | null;
  sessionStart: string | null;
  state: number | null;
}

const ACTIVE_SESSION_POLL_MS = 3_000;
// Safety cap (~1 min at 3s) so we don't poll forever on an encounter that never
// becomes active (e.g. viewing a finished/old chart).
const ACTIVE_SESSION_MAX_POLLS = 20;

/**
 * The visit page can render before the Take transition opens the state machine,
 * so the first response may be inactive (or 404 while no state rows exist yet).
 * Poll until a provider is actively charting, then stop; give up after a cap.
 */
export const getActiveSessionRefetchInterval = (
  isActive: boolean | undefined,
  pollCount: number,
): number | false =>
  isActive || pollCount >= ACTIVE_SESSION_MAX_POLLS
    ? false
    : ACTIVE_SESSION_POLL_MS;

/**
 * Fetches the active charting-session state for the given encounter from the
 * new_api backend.
 *
 * - `isActive` true means a provider is actively charting; `sessionStart` is
 *   the authoritative start of their current unbroken session.
 * - `isActive` false means the encounter is released, handed-off, or closed;
 *   the visit timer must NOT be shown.
 *
 * Polls until a session becomes active (handles the visit page loading before
 * the Take opens the state machine), then stops.
 *
 * The query key is prefixed with `["patient", "chart", patientId]`, so it is
 * automatically invalidated by `invalidatePatientQueries` in
 * `useEncounterTransitions` whenever the provider pauses, hands off, steals,
 * or closes the encounter.
 */
export const useEncounterActiveSession = ({
  patientId,
  encounterId,
}: {
  patientId?: string;
  encounterId?: string;
}) =>
  useApi<EncounterActiveSession>({
    queryOptions: {
      queryFn: async () =>
        (
          await axiosInstance.get(
            `/new-api/encounter/${encounterId}/active-session`,
          )
        ).data,
      queryKey: QueryKeys.Patient.Chart.ACTIVE_SESSION({
        patientId,
        encounterId,
      }),
      enabled: Boolean(patientId) && Boolean(encounterId),
      refetchInterval: (query) =>
        getActiveSessionRefetchInterval(
          query.state.data?.isActive,
          query.state.dataUpdateCount + query.state.errorUpdateCount,
        ),
    },
  });
